import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  IconButton,
  Collapse,
} from "@mui/material";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";

import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useTheme } from "@mui/material/styles";

// Define interfaces for type safety
interface MetricData {
  value: number;
  label?: string;
  positives?: string[];
  recommendations?: string[];
}

interface Simulation {
  evaluation: Record<string, MetricData>;
}

interface EvaluationMetricProps {
  simulation: Simulation;
  focus?: string;
  isMobile: boolean;
  mode?: "light" | "dark";
}

interface SectionHeaderProps {
  title: string;
  sectionKey: string;
  metricKey: string;
  expanded: boolean;
  onToggle: () => void;
  isMobile: boolean;
  titleColour: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  sectionKey,
  metricKey,
  expanded,
  onToggle,
  isMobile,
  titleColour,
}) => {
  const theme = useTheme();

  if (!isMobile) {
    return (
      <Typography
        sx={{
          fontSize: {
            xs: theme.typography.textMD.fontSize,
            sm: theme.typography.textLG.fontSize,
            md: theme.typography.textXL.fontSize,
          },
          fontWeight: theme.typography.fontWeightMedium,
          mb: { xs: 1, md: 0 },
          color: titleColour,
        }}
      >
        {title}
      </Typography>
    );
  }

  return (
    <Box
      role="button"
      aria-expanded={expanded}
      aria-controls={`${metricKey}-${sectionKey}-panel`}
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <Typography
        sx={{
          fontSize: {
            xs: theme.typography.textMD.fontSize,
            sm: theme.typography.textLG.fontSize,
            md: theme.typography.textXL.fontSize,
          },
          fontWeight: theme.typography.fontWeightMedium,
          color: titleColour,
        }}
      >
        {title}
      </Typography>
      <IconButton
        size="small"
        aria-label={expanded ? "Collapse section" : "Expand section"}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        sx={{
          ml: 1,
          p: 0.5,
          transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease",
          color: titleColour,
        }}
      >
        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 28 }} />
      </IconButton>
    </Box>
  );
};

interface SectionBodyProps {
  children: React.ReactNode;
  metricKey: string;
  sectionKey: string;
  expanded: boolean;
  isMobile: boolean;
}

const SectionBody: React.FC<SectionBodyProps> = ({
  children,
  metricKey,
  sectionKey,
  expanded,
  isMobile,
}) => {
  if (!isMobile) {
    return <Box id={`${metricKey}-${sectionKey}-panel`}>{children}</Box>;
  }

  return (
    <Collapse
      in={expanded}
      unmountOnExit
      id={`${metricKey}-${sectionKey}-panel`}
    >
      {children}
    </Collapse>
  );
};

const Recommendations: React.FC<EvaluationMetricProps> = ({
  simulation,
  focus,
  isMobile,
  mode = "light",
}) => {
  const theme = useTheme();

  const titleColour = mode === "dark" ? theme.palette.text.primary : "#02002B";

  const [openMap, setOpenMap] = React.useState({});
  const setOpen = (metricKey, section, value) =>
    setOpenMap((prev) => ({
      ...prev,
      [metricKey]: { ...(prev[metricKey] || {}), [section]: value },
    }));
  const toggle = (metricKey, section) =>
    setOpen(metricKey, section, !(openMap[metricKey] || {})[section]);

  function setLabel(metricKey) {
    return metricKey
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return (
    <>
      {Object.entries(simulation.evaluation).map(([metricKey, metricData]) => {
        if (metricKey === "total_score" || metricKey === "engine_name") {
          return null;
        }

        if (focus && focus !== metricKey) return null;

        const hasPos = (metricData.positives?.length ?? 0) > 0;
        const hasRec = (metricData.recommendations?.length ?? 0) > 0;
        const shouldDisplay = "flex";

        return (
          <Box
            key={metricKey}
            sx={{
              display: shouldDisplay,
              flexDirection: "column",
              p: theme.customSpacing["3xl"],
            }}
          >
            {/* Score header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                mb: theme.customSpacing["4xl"],
              }}
            >
              <Box sx={{ width: 200, height: "110px" }}>
                <CircularProgressbar
                  value={metricData.value}
                  text={(metricData.value / 10).toFixed(1)}
                  strokeWidth={12.5}
                  circleRatio={0.5}
                  styles={buildStyles({
                    rotation: 0.75,
                    strokeLinecap: "round",
                    pathColor: theme.palette.border.brand,
                    trailColor: theme.palette.border.secondary,
                    textColor: titleColour,
                    pathTransition: "stroke-dashoffset 0.5s ease 0s",
                  })}
                />
              </Box>
              <Typography
                sx={{
                  mt: theme.customSpacing["xl"],
                  fontSize: theme.typography.displayXS.fontSize,
                  fontWeight: theme.typography.fontWeightMedium,
                }}
              >
                {metricData?.label || setLabel(metricKey)}
              </Typography>
            </Box>

            {/* Sections container */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                width: "100%",
                gap: { xs: 2, md: 0 },
                "@media (max-width:1080px)": {
                  flexDirection: "column",
                },
              }}
            >
              {hasPos && (
                <Box sx={{ flex: 1, minWidth: 0, pr: { md: 2 } }}>
                  <SectionHeader
                    title="Here's what went well 👏"
                    sectionKey="positives"
                    metricKey={metricKey}
                    expanded={(openMap[metricKey] || {})["positives"] || false}
                    onToggle={() => toggle(metricKey, "positives")}
                    isMobile={isMobile}
                    titleColour={titleColour}
                  />
                  <SectionBody
                    metricKey={metricKey}
                    sectionKey="positives"
                    expanded={(openMap[metricKey] || {})["positives"] || false}
                    isMobile={isMobile}
                  >
                    <List
                      dense
                      sx={{
                        width: "100%",
                        "& > li": {
                          marginBottom: "10px",
                          "&:last-child": { marginBottom: 0 },
                        },
                      }}
                    >
                      {metricData.positives?.map((item, index) => (
                        <ListItem
                          key={`${metricKey}-positive-${index}`}
                          alignItems="flex-start"
                          sx={{
                            p: 0,
                            minHeight: "48px",
                            alignItems: "flex-start",
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              variant="square"
                              sx={{
                                backgroundColor:
                                  theme.palette.background.button_primary,
                                color: theme.palette.background.primary,
                                borderRadius: 1,
                                height: "24px",
                                width: "24px",
                              }}
                            >
                              <ThumbUpAltOutlinedIcon
                                sx={{
                                  fontSize: theme.typography.textMD.fontSize,
                                }}
                              />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            sx={{
                              "& span": {
                                fontWeight: 400,
                              },
                            }}
                            primary={item}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </SectionBody>
                </Box>
              )}

              {hasPos && hasRec && (
                <>
                  <Divider
                    sx={{
                      display: { xs: "block", md: "none" },
                      my: 1,
                      height: "1px",
                      backgroundColor: "#E9EAEB",
                      width: "100%",
                    }}
                  />
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      display: { xs: "none", md: "block" },
                      backgroundColor: "#E9EAEB",
                      mx: 4,
                      width: "1px",
                    }}
                  />
                </>
              )}

              {hasRec && (
                <Box sx={{ flex: 1, minWidth: 0, pl: { md: 2 } }}>
                  <SectionHeader
                    title="Try these recommendations 💪"
                    sectionKey="recommendations"
                    metricKey={metricKey}
                    expanded={(openMap[metricKey] || {})["recommendations"] || false}
                    onToggle={() => toggle(metricKey, "recommendations")}
                    isMobile={isMobile}
                    titleColour={titleColour}
                  />
                  <SectionBody
                    metricKey={metricKey}
                    sectionKey="recommendations"
                    expanded={(openMap[metricKey] || {})["recommendations"] || false}
                    isMobile={isMobile}
                  >
                    <List
                      dense
                      sx={{
                        width: "100%",
                        "& > li": {
                          marginBottom: "10px",
                          "&:last-child": { marginBottom: 0 },
                        },
                      }}
                    >
                      {metricData.recommendations?.map((item, index) => (
                        <ListItem
                          key={`${metricKey}-recommendation-${index}`}
                          alignItems="flex-start"
                          sx={{
                            p: 0,
                            minHeight: "48px",
                            alignItems: "flex-start",
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              variant="square"
                              sx={{
                                backgroundColor:
                                  theme.palette.background.button_primary,
                                color: theme.palette.background.primary,
                                borderRadius: 1,
                                height: "24px",
                                width: "24px",
                              }}
                            >
                              <TrendingUpIcon
                                sx={{
                                  fontSize: theme.typography.textMD.fontSize,
                                }}
                              />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            sx={{
                              "& span": {
                                fontWeight: 400,
                              },
                            }}
                            primary={item}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </SectionBody>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default Recommendations;
